import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { InputField, SwitchField } from 'app/components/Core';
import {
  Button,
  Grid,
  GridItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { useIpnDialogSubmit } from 'app/components/Profile/Ids/IpnDialog/hooks';
import { getPlayerHeight } from 'app/store/AudioPlayer/selectors';
import { useAppSelector } from 'app/store';
import { SocietySelect } from '../SocietySelect';
import {
  getAddIpnValidationSchema,
  getDefaultValues,
  getUpdateIpnValidationSchema,
} from './utils';
import { toSocietyOptions } from '../utils';
import { IpnDialogComponentProps, IpnForm } from './types';

export const IpnDialogComponent: React.FC<IpnDialogComponentProps> = ({
  open,
  societies,
  ipn,
  areSocietiesLoading,
  ipnValidations,
  updateIpn,
  addIpnManually,
  handleAddIpnV2,
  handleAddIpnV3,
  onClose,
}) => {
  const playerHeight = useAppSelector(getPlayerHeight);
  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting, isDirty, dirtyFields, errors },
    reset,
    watch,
    setValue,
    trigger,
    register,
  } = useForm<IpnForm>({
    defaultValues: getDefaultValues(ipn),
    mode: 'all',
    resolver: yupResolver(
      ipn
        ? getUpdateIpnValidationSchema(ipnValidations)
        : getAddIpnValidationSchema(ipnValidations)
    ),
  });
  const handleClose = (status?: number) => {
    onClose(status);
    reset();
  };

  const { onSubmit } = useIpnDialogSubmit({
    handleAddIpnV2,
    handleAddIpnV3,
    addIpnManually,
    ipn,
    updateIpn,
    dirtyFields,
    handleClose,
    handleSubmit,
  });

  const watchSociety = watch('society');

  useEffect(() => {
    if (watchSociety?.verification || !watchSociety) {
      setValue('ipnNumber', '');
    }
  }, [setValue, watchSociety]);

  const showDetailsFields = watchSociety && !watchSociety?.verification;

  return (
    <Modal isOpen={open} isCentered onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent bottom={`${playerHeight}px`}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return onSubmit(e);
          }}
          data-testid="ipn-form"
        >
          <ModalHeader
            data-testid="dialog-title"
            title={`${ipn ? 'Edit' : 'Add'} IPI Number`}
          >
            {ipn ? 'Edit' : 'Add'} IPN Number
          </ModalHeader>
          <ModalBody data-testid="dialog-content">
            <Grid
              columnGap={4}
              templateRows={`repeat(${showDetailsFields ? 3 : 1}, 1fr)`}
              templateColumns="repeat(2, 1fr)"
            >
              <GridItem colSpan={2}>
                <SocietySelect
                  name="society"
                  value={watchSociety}
                  control={control}
                  options={toSocietyOptions(societies)}
                  isDisabled={Boolean(ipn)}
                  loading={areSocietiesLoading}
                />
              </GridItem>
              {showDetailsFields && (
                <>
                  <GridItem>
                    <InputField
                      label="First Name"
                      {...register('firstName', {
                        onChange: () => trigger('lastName'),
                        onBlur: () => trigger('lastName'),
                      })}
                      isInvalid={Boolean(errors.firstName)}
                      errorMessage={errors.firstName?.message}
                    />
                  </GridItem>
                  <GridItem>
                    <InputField
                      label="Last Name"
                      {...register('lastName', {
                        onChange: () => trigger('firstName'),
                        onBlur: () => trigger('firstName'),
                      })}
                      isInvalid={Boolean(errors.lastName)}
                      errorMessage={errors.lastName?.message}
                    />
                  </GridItem>
                  <GridItem>
                    <InputField
                      label="IPN Number"
                      {...register('ipnNumber')}
                      isDisabled={Boolean(ipn)}
                      isInvalid={Boolean(errors.ipnNumber)}
                      errorMessage={errors.ipnNumber?.message}
                    />
                  </GridItem>
                  <GridItem>
                    <SwitchField
                      label="Use as Default IPN"
                      labelProps={{
                        htmlFor: 'isDefault',
                      }}
                      data-testid="default-label"
                      {...register('isDefault')}
                    />
                  </GridItem>
                </>
              )}
            </Grid>
          </ModalBody>
          <ModalFooter data-testid="dialog-actions">
            <Button
              onClick={() => handleClose()}
              variant="secondary"
              data-testid="IpnDialog-cancel-button"
            >
              Cancel
            </Button>
            <Button
              ml={2}
              type="submit"
              isDisabled={
                !isValid || isSubmitting || (Boolean(ipn) && !isDirty)
              }
              data-testid="IpnDialog-save-button"
            >
              {ipn ? 'Save' : 'Add'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
